// Incremental body-fetch window for the skill drift lens (SkillDetailBody).
//
// The lens pages a growing trace window and reads each prompt's SKILL.md body
// from signal_tags, fetching only ids that entered the window since the last
// fetch and accumulating them. This is the id-state machine behind that — kept
// pure so its subtlest paths (release on failure / supersession, so a dropped
// fetch is retried instead of stranding those prompts out of the drift view)
// are unit-testable without a DOM.
//
// An id is in one of three states: unseen (fetch it), `inflight` (a request is
// out — don't re-request), or `fetched` (a response landed — never re-request).
// A run marks its ids inflight, then on resolve either GRADUATES them to fetched
// (ok) or RELEASES them back to unseen (failed / superseded) so a later window
// recomputes them as pending and retries. Nothing reaches `fetched` without a
// successful response, so a dropped run can't strand bodies.

export function emptyBodyWindow() {
  return { fetched: new Set(), inflight: new Set() };
}

// Ids in the window that still need a fetch: neither resolved nor already in
// flight. This is the delta a growing window requests — never the whole window.
export function pendingBodyIds(windowIds, win) {
  if (!Array.isArray(windowIds) || !windowIds.length) return [];
  return windowIds.filter((id) => !win.fetched.has(id) && !win.inflight.has(id));
}

// Ids in the window without a resolved body yet (in-flight counts as unresolved)
// — drives the "reading…" status so it doesn't flash "no body" mid-page.
export function unresolvedBodyIds(windowIds, win) {
  if (!Array.isArray(windowIds) || !windowIds.length) return [];
  return windowIds.filter((id) => !win.fetched.has(id));
}

// Mark ids as in flight (returns a new window; inputs are not mutated).
export function startFetch(win, ids) {
  const inflight = new Set(win.inflight);
  ids.forEach((id) => inflight.add(id));
  return { fetched: win.fetched, inflight };
}

// Resolve a fetch. `ok` true graduates the ids to `fetched` (they'll never be
// re-requested, even if their response carried no body). `ok` false — a failed
// execute, or a superseded run — only releases them from `inflight`, so a later
// window recomputes them as pending and retries.
export function resolveFetch(win, ids, ok) {
  const inflight = new Set(win.inflight);
  ids.forEach((id) => inflight.delete(id));
  if (!ok) return { fetched: win.fetched, inflight };
  const fetched = new Set(win.fetched);
  ids.forEach((id) => fetched.add(id));
  return { fetched, inflight };
}
